import type { GridApiPremium, GridColDef, GridDataSource } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import { GRID_DEFAULT_PAGE_SIZE } from "@entrilia/shared/components/grid/gridHelper";
import InlineItemsList from "@entrilia/shared/components/InlineItemsList";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import { logError } from "@entrilia/shared/logging";
import type {
  InvestorCommunicationFund,
  InvestorCommunicationInfo,
} from "../../../api/types/investorCommunicationsTypes";
import storage from "../../../storage/storage";
import CategoryInlineTags from "../../common/CategoryInlineTags";
import UserWithTimestamp from "../../common/UserWithTimestamp";
import InvestorCommunicationStatusTag from "@entrilia/shared/components/InvestorCommunicationStatusTag";
import InvestorCommunicationsGridActionsCell from "./InvestorCommunicationsGridActionsCell";
import InvestorCommunicationTitleCell from "./InvestorCommunicationTitleCell";

const columnDefinitions: GridColDef<InvestorCommunicationInfo>[] = [
  {
    field: "title",
    headerName: "Title",
    sortable: false,
    flex: 1.5,
    minWidth: 200,
    renderCell: ({ row }) => (
      <GridTextHoverLink to={row.id}>
        <InvestorCommunicationTitleCell communication={row} />
      </GridTextHoverLink>
    ),
  },
  {
    field: "status",
    headerName: "Status",
    sortable: false,
    width: 120,
    renderCell: ({ row }) => <InvestorCommunicationStatusTag status={row.status} />,
  },
  {
    field: "funds",
    headerName: "Funds",
    sortable: false,
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => (
      <InlineItemsList<InvestorCommunicationFund> items={row.funds} displayCount={1} propGetter={(fund) => fund.name} />
    ),
  },
  {
    field: "categoryIds",
    headerName: "Categories",
    sortable: false,
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => (
      <CategoryInlineTags externalCategoryIds={row.categoryIds} maxDisplayedCategories={1} noWrap />
    ),
  },
  {
    field: "createdAt",
    headerName: "Created",
    sortable: false,
    flex: 1,
    minWidth: 180,
    renderCell: ({ row }) => <UserWithTimestamp userName={row.createdByUserName} timestamp={row.createdAt} />,
  },
  {
    field: "publishedToLiveAt",
    headerName: "Published",
    sortable: false,
    flex: 1,
    minWidth: 180,
    renderCell: ({ row }) =>
      row.publishedToLiveAt && row.publishedByUserName ? (
        <UserWithTimestamp userName={row.publishedByUserName} timestamp={row.publishedToLiveAt} />
      ) : (
        "—"
      ),
  },
  {
    field: "actions",
    headerName: "Actions",
    sortable: false,
    width: 80,
    align: "right",
    headerAlign: "right",
    cellClassName: "grid-row-actions",
    renderCell: ({ row }) => <InvestorCommunicationsGridActionsCell communication={row} />,
  },
];

interface Props {
  apiRef: RefObject<GridApiPremium | null>;
  dataSource: GridDataSource;
  filterId: string;
}

const InvestorCommunicationsGrid = ({ apiRef, dataSource, filterId }: Props) => {
  const [dataSourceError, setDataSourceError] = useState<Error>();

  const handleDataSourceError = useCallback(
    (error: Error) => {
      logError(error, "[InvestorCommunicationsGrid]");
      setDataSourceError(error);
      storage.clearFilterState(filterId);
    },
    [filterId]
  );

  if (dataSourceError) {
    return <DataLoadingFailed title="Loading investor communications failed" />;
  }

  return (
    <DataGrid<InvestorCommunicationInfo>
      apiRef={apiRef}
      columns={columnDefinitions}
      lazyLoading
      dataSource={dataSource}
      paginationModel={{ page: 0, pageSize: GRID_DEFAULT_PAGE_SIZE }}
      onDataSourceError={handleDataSourceError}
      multilineRows
      noRowsText="No investor communications"
      disableColumnSorting
      disableColumnReorder
    />
  );
};

export default InvestorCommunicationsGrid;
